import { useMemo } from "react";
import { useCurrentCompanyValue } from "hooks/auth";
import type { ScopeType } from "../types/RolesPermissions.type";

/**
 * Every scope type the picker could ever offer, in cascade order
 * (broadest first) — the shape `ScopePicker` iterates and the order its
 * lists appear in. `global` is NOT here: it is the mode toggle in
 * `AccessModeSelector`, never one of the picker's lists (CONTRACT §3.1).
 */
export const ALL_PICKER_SCOPE_TYPES: ScopeType[] = ["project_type", "project", "rfx"];

/**
 * The picker lists a BIDDER-SIDE role may be granted at.
 *
 * A bidder role is a role for working on someone else's tender, and the unit
 * of that work is the individual RFx event. Portfolios and projects are the
 * procurer's own structure for organising the tenders they run; granting a
 * bidder role over one would say "this person has bidder standing across an
 * entire project", which is not a relationship that exists.
 */
export const BIDDER_ROLE_SCOPE_TYPES: ScopeType[] = ["rfx"];

/** `roles.side` — the enum the API serves on every `RoleResource`. */
export const BIDDER_ROLE_SIDE = "bidder";

/**
 * Which per-type lists the company the admin is acting as can be offered AT
 * ALL, before any role is chosen.
 *
 * PRO-757 follow-up — author report: "when company is Bidder only they have no
 * 'Project Types' or 'Projects'. We should hide these selectors and just show
 * the RFx's they have been invited to."
 *
 * Portfolios and projects are procurer-OWNED structure: the backend catalogue
 * filters both on `company_id` alone, so a bidder-only company has no rows of
 * either and the picker rendered two lists that could only ever say "No
 * matches.". RFx is the exception and already worked — a bidder owns none of
 * the RFx it bids on either, so the catalogue admits them via `rfx_invites`
 * (PRO-719).
 *
 * The decision is the SERVER'S: `company.rbac_scope_types` is computed by
 * `ScopeOptionCatalog::offerableScopeTypes()` from the account type's RBAC
 * side, which is the documented single source of truth for the
 * procurer/bidder/hybrid split (and the only map that covers the sponsored and
 * legacy account types). Deriving it here from `account_type_id` instead would
 * duplicate that map in TypeScript, which is exactly what
 * `has_project_type_grouping` is computed server-side to avoid.
 *
 * Two deliberate properties:
 *  - it reads `state.auth.currentCompany`, which is re-fetched per company on
 *    switch, so an executive acting as another company gets THAT company's
 *    answer — the same source `ScopePicker` already reads
 *    `has_project_type_grouping` from;
 *  - while the company has not loaded (`null`), or on an older payload without
 *    the key, it falls back to ALL types. That is today's behaviour, and it is
 *    the right way round to be wrong: showing a bidder an empty list is
 *    cosmetic, whereas hiding portfolios from a procurer would hide real,
 *    grantable scopes.
 */
export const useCompanyScopeTypes = (): ScopeType[] => {
	const offerable = useCurrentCompanyValue("rbac_scope_types");

	// Collapsed to a CONTENT key before the memo, and the memo then derives its
	// answer from that key alone — so `key` is a genuinely exhaustive dependency
	// (no eslint-disable, which this repo's standards forbid outright) and the
	// returned array keeps a stable identity across renders.
	//
	// The stability matters: `AddAccessFlow` feeds this into a `useMemo` that
	// rebuilds the cascade tour's steps, and `useGuidedTour` restarts a tour whose
	// steps change — a fresh `filter()` result every render would leave that tour
	// unable to settle. Scope-type values never contain a comma (the union is
	// "global" | "project_type" | "project" | "rfx"), so joining is lossless.
	const key = offerable && offerable.length > 0 ? offerable.join(",") : "";

	return useMemo(() => {
		if (key === "") {
			return ALL_PICKER_SCOPE_TYPES;
		}

		const allowed = key.split(",");

		// Intersect rather than trust the order/content of the payload: the
		// picker's own cascade order is what the exclusion logic in `ScopePicker`
		// depends on (`ancestorExclude` slices by index), so it must never be
		// reordered by a server response, and `global` must never leak in as a list.
		return ALL_PICKER_SCOPE_TYPES.filter((type) => allowed.includes(type));
	}, [key]);
};

/**
 * Which per-type lists the picker should render, for this company AND the role
 * currently selected.
 *
 * PRO-757 follow-up — author: "remove projects from the Bidder security role
 * management too — so if a hybrid or bidder company select a bidder role only
 * let RFx's they have been invited to be displayed, not projects."
 *
 * Two independent narrowings, intersected:
 *
 *  1. THE COMPANY ({@see useCompanyScopeTypes}) — what this company owns any
 *     of. Server-decided from the account type.
 *  2. THE ROLE — what the selected role is *for*. A `side: "bidder"` role
 *     narrows to RFx alone even on a HYBRID company that genuinely owns
 *     portfolios and projects, because the narrowing is about the role's
 *     meaning, not about whether rows exist to show.
 *
 * `side` is already served on every `RoleResource` (`procurer | bidder |
 * both`) and is inherited by custom roles through `RoleService::clone()`, so a
 * company's own clone of Bidder Support narrows exactly like the base role.
 * Nothing new is derived here: an unrecognised, absent or null side narrows
 * NOTHING, so an API build that stops sending the column degrades to the
 * company-only answer rather than to an RFx-only picker.
 *
 * SCOPE OF THIS RULE — it is LAYOUT, not authorisation. The server does not
 * yet refuse a project-scoped grant of a bidder role on a company that owns
 * the project (`AssignmentService::assertGrantable()` asks only whether the
 * scope is reachable BY THE COMPANY, which for a hybrid it is). Hiding the
 * lists stops the flow offering it; it does not stop a direct API call. That
 * enforcement is deliberately deferred — author: "We will do that later."
 */
export const useOfferableScopeTypes = (roleSide?: string | null): ScopeType[] => {
	const companyTypes = useCompanyScopeTypes();

	// Same content-key discipline as above: `companyTypes` is already memo-stable,
	// and `roleSide` is a primitive, so both deps are exhaustive and the result
	// keeps a stable identity for the tour-step memo downstream.
	return useMemo(() => {
		if (roleSide !== BIDDER_ROLE_SIDE) {
			return companyTypes;
		}

		const narrowed = companyTypes.filter((type) => BIDDER_ROLE_SCOPE_TYPES.includes(type));

		// A company that cannot be offered RFx at all would otherwise end up with
		// an empty picker and no explanation. Nothing in the product produces that
		// today (every side is offered `rfx`), but falling back to the company's
		// own answer keeps the failure cosmetic rather than a dead screen.
		return narrowed.length > 0 ? narrowed : companyTypes;
	}, [companyTypes, roleSide]);
};
